import {
    ACTIVE_SCREEN_KEY,
    formatMessage,
    hideDialog,
    hideDrawer,
    setErrorScreenMessage,
    setScreenData,
    showDialog,
} from 'cw-ui-core';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import size from 'lodash/size';
import get from 'lodash/get';
import { all, call, put } from 'redux-saga/effects';
import { ALERT_TYPE } from 'src/asio-neon-components/Constants';
import setAlertScreenMessage from 'src/asio-neon-components/Utils';
import { DEFAULT_LOADING_DIALOG } from 'src/components/DefaultLoadingDialog/DefaultLoadingDialog';
import { convertDomainInfoToTreeFormat } from 'src/features/application-roles/components/GranularPremission/utils';
import Locale from 'src/i18n';
import { DELETE, GET, getHeaders, getUrl, POST, PUT } from 'src/services/ApiService';
import apiClient from './apiClient';

export const PERMISSIONS_API_CACHE_ID = 'cache:domain_permissions';

export function* fetchPermissionsForDomains(clientId, domains, screenKey = ACTIVE_SCREEN_KEY) {
    if (isEmpty(clientId) || isEmpty(domains)) return;

    yield put(setScreenData(['isErrorFetchingDomainPermissions'], false, screenKey));

    try {
        const fetchAllDomainInfoSaga = map(domains, domain => {
            const endpoint = `/iambff/v1/connected-apps/${clientId}/domains/${domain?.id}`;
            return call(apiClient.get, getUrl(endpoint), {
                cache: {
                    ttl: 1000 * 60 * 5, // 5 minutes
                    id: PERMISSIONS_API_CACHE_ID,
                },
            });
        });
        const permissionsResult = yield all(fetchAllDomainInfoSaga);
        const sagasToConvertPermissions = map(permissionsResult, response => {
            return call(convertDomainInfoToTreeFormat, response.data ?? {});
        });
        const convertedData = yield all(sagasToConvertPermissions);

        const sagasToPutDataInRedux = map(convertedData, ({ tree }, index) => {
            return put(setScreenData(['domains', domains[index]?.id, 'permissions'], tree, screenKey));
        });

        const sagasToPutSelectedNodesDataInRedux = map(convertedData, ({ selectedPermissions }, index) => {
            return put(
                setScreenData(
                    ['domains', domains[index]?.id, 'selectedPermissions'],
                    { ...selectedPermissions },
                    screenKey
                )
            );
        });

        // we can run them in parallel
        yield all(sagasToPutDataInRedux);

        yield put(setScreenData(['selectedPermissionsProcessed'], false, screenKey));

        yield all(sagasToPutSelectedNodesDataInRedux);

        yield put(setScreenData(['selectedPermissionsProcessed'], true, screenKey));
    } catch (e) {
        yield put(setScreenData(['isErrorFetchingDomainPermissions'], true, screenKey));
        yield put(
            setAlertScreenMessage(
                ALERT_TYPE.error,
                '',
                formatMessage(Locale.problemFetchingPermissions),
                true,
                screenKey
            )
        );
    }
}

export default function* fetchDomainsWithPermissions(clientId, screenKey = ACTIVE_SCREEN_KEY) {
    if (isEmpty(clientId)) return;

    // it will take domains to loading state
    yield put(setScreenData(['domainsLoading'], true, screenKey));

    // fetch domains based on clientId and set them in redux
    const result = yield call(GET, `/iambff/v1/connected-apps/${clientId}/domains`, getHeaders());
    const sortedDomains = [];
    if (result.isSuccess) {
        for (let i = 0; i < size(result.response?.domains); i += 1) {
            sortedDomains.push(result.response?.domains[i]?.id);
            yield put(
                setScreenData(['domains', result.response?.domains[i]?.id], result.response?.domains[i], screenKey)
            );
        }
        yield put(setScreenData(['sortedDomains'], sortedDomains, screenKey));
    } else {
        yield put(setAlertScreenMessage(ALERT_TYPE.error, '', formatMessage(Locale.problemFetchingDomains), true));
    }

    // it will make loading state false for domains
    yield put(setScreenData(['domainsLoading'], false, screenKey));

    // Enhancement: Need to think what should happen if we failed to fetch list of domains
    if (result.isSuccess) {
        // it will take permissions to loading state
        yield put(setScreenData(['permissionsLoading'], true, screenKey));

        // it will fetch permissions for all the domains asynchronously and set them in redux
        yield call(fetchPermissionsForDomains, clientId, result?.response?.domains, screenKey);

        // it will make loading state false for permissions
        yield put(setScreenData(['permissionsLoading'], false, screenKey));
    }
}

export function* loadAppRolePermissions(clientId, roleId, screenKey = ACTIVE_SCREEN_KEY) {
    const isRolePermissionsReady = ['isRolePermissionsReady'];

    yield put(setScreenData(isRolePermissionsReady, false, screenKey));

    const result = yield call(GET, `/iambff/v1/connected-apps/${clientId}/app-roles/${roleId}`, getHeaders());
    if (result.isSuccess) {
        const legacyPermissions = get(result.response, 'legacyPermissions');
        const legacyRoleIdRef = get(result.response, 'legacyRoleId');
        const platformPermissions = get(result.response, 'platformPermissions');

        yield put(setScreenData(['roleDetails', 'legacyPermissions'], legacyPermissions, screenKey));
        yield put(setScreenData(['roleDetails', 'legacyRoleIdRef'], legacyRoleIdRef, screenKey));
        yield put(setScreenData(['roleDetails', 'platformPermissions'], platformPermissions, screenKey));
    } else {
        yield put(setAlertScreenMessage(ALERT_TYPE.error, '', formatMessage(Locale.problemFetchingPermissions), true));
    }
    yield put(setScreenData(isRolePermissionsReady, true));
}

export function* fetchRoleDetails(appId, roleId, screenKey = ACTIVE_SCREEN_KEY) {
    yield put(setScreenData(['roleDetailsLoading'], true));
    const result = yield call(GET, `/iambff/v1/connected-apps/${appId}/app-roles/${roleId}`, getHeaders());
    if (result.isSuccess) {
        yield put(setScreenData(['activeRole', 'selectedPermissions'], result.response, screenKey));
    } else {
        yield put(
            setAlertScreenMessage(
                ALERT_TYPE.error,
                '',
                formatMessage(Locale.errorFetchingApplicationRole),
                true,
                screenKey
            )
        );
    }
    yield put(setScreenData(['roleDetailsLoading'], false));
}

export function* deleteCustomRole(roleId, appId, screenKey = ACTIVE_SCREEN_KEY) {
    let isError = false;
    yield put(hideDialog(screenKey));
    const result = yield call(DELETE, `/iambff/v1/connected-apps/${appId}/app-roles/${roleId}`, getHeaders());
    yield put(hideDialog(DEFAULT_LOADING_DIALOG));

    if (result.isSuccess) {
        yield put(
            setAlertScreenMessage(
                ALERT_TYPE.success,
                '',
                formatMessage(Locale.appRoleDeletedSuccessfully),
                true,
                ACTIVE_SCREEN_KEY
            )
        );
    } else {
        isError = true;
        yield put(setAlertScreenMessage(ALERT_TYPE.error, '', result.error, true, ACTIVE_SCREEN_KEY));
    }

    return { isError };
}

export function* cloneRoleService(
    clientId,
    roleDetails,
    selectedPermissions,
    requestedBy,
    screenKey = ACTIVE_SCREEN_KEY,
    cloneContentsAssignmentAsWell = false
) {
    const requestBody = {
        ...roleDetails,
        requestedBy,
        ...selectedPermissions,
        cloneRoleContent: cloneContentsAssignmentAsWell,
    };
    yield put(showDialog(DEFAULT_LOADING_DIALOG));
    let isError = false;

    const result = yield call(POST, `/iambff/v1/connected-apps/${clientId}/app-roles`, getHeaders(), requestBody);
    if (result.isSuccess) {
        yield put(
            setAlertScreenMessage(
                ALERT_TYPE.success,
                '',
                formatMessage(Locale.appRoleClonedSuccessful),
                true,
                screenKey
            )
        );
    } else {
        yield put(setAlertScreenMessage(ALERT_TYPE.error, '', result?.error, true, screenKey));
        isError = true;
    }

    yield put(hideDialog(DEFAULT_LOADING_DIALOG));

    return {
        isError,
    };
}

export function* editRoleService(
    clientId,
    roleDetails,
    selectedPermissions,
    currentRoleId,
    requestedBy,
    screenKey = ACTIVE_SCREEN_KEY
) {
    const requestBody = {
        ...roleDetails,
        ...selectedPermissions,
        requestedBy,
    };

    yield put(showDialog(DEFAULT_LOADING_DIALOG));
    let isError = false;

    const result = yield call(
        PUT,
        `/iambff/v1/connected-apps/${clientId}/app-roles/${currentRoleId}`,
        getHeaders(),
        requestBody
    );
    if (result.isSuccess) {
        yield put(hideDrawer(screenKey));

        yield put(
            setAlertScreenMessage(
                ALERT_TYPE.success,
                '',
                formatMessage(Locale.appRoleUpdatedSuccessfully, { roleName: requestBody?.displayName || '' }),
                true,
                ACTIVE_SCREEN_KEY
            )
        );
    } else {
        yield put(
            setAlertScreenMessage(
                ALERT_TYPE.error,
                '',
                formatMessage(Locale.failedToEditApplicationRole),
                true,
                screenKey
            )
        );
        isError = true;
    }

    yield put(hideDialog(DEFAULT_LOADING_DIALOG));

    return {
        isError,
    };
}

export function* detectDuplicateRolesService(clientId, selectedPermissions, roleId = '') {
    yield put(showDialog(DEFAULT_LOADING_DIALOG));
    const requiredPayload = { ...selectedPermissions, roleId };
    const result = yield call(
        POST,
        `/iambff/v1/connected-apps/${clientId}/duplicate-roles`,
        getHeaders(),
        requiredPayload
    );

    yield put(hideDialog(DEFAULT_LOADING_DIALOG));

    return { duplicateRoles: result.response?.roles, isError: !result.isSuccess };
}

export function* fetchUsersForAppRole(connectedApp, appRoleId, screenKey = ACTIVE_SCREEN_KEY) {
    yield put(setScreenData('isUsersForAppRoleLoading', true, screenKey));
    const result = yield call(
        GET,
        `/iambff/v1/connected-apps/${connectedApp}/app-roles/${appRoleId}/users`,
        getHeaders()
    );
    if (result.isSuccess) {
        yield put(setScreenData('selectedUsers', result.response.userIds, screenKey));
        yield put(setScreenData('fetchedUsers', result.response.userIds, screenKey));
    } else {
        setErrorScreenMessage(result.errors[0].screenMessage, false, result.errors[0].errorMessages, screenKey);
    }
    yield put(setScreenData('isUsersForAppRoleLoading', false, screenKey));
}
